import { useCallback, useEffect, useState } from "react";
import { RadioButton } from "../../../components/RadioButton/RadioButton.tsx";
import { useUpdateUser } from "../../Home/HomeIndex/hooks/useUpdateUser.ts";
import {
  ANIMATION_AND_GAMING_INDUSTRY,
  BEAUTY_AND_SKINCARE_INDUSTRY,
  CREATIVE_OR_COMMUNICATION_AGENCIES_INDUSTRY,
  CUSTOMIZATION_STEP,
  CUSTOMIZATION_STEP_INDUSTRY,
  CUSTOMIZATION_STEP_OCCUPATION,
  FASHION_INDUSTRY,
  FOOD_AND_BEVERAGE_INDUSTRY,
  INDUSTRY_OPTIONS,
  OTHER_INDUSTRY,
  REAL_ESTATE_INDUSTRY,
  RETAIL_INDUSTRY_AND_CONSUMER_GOODS,
  TECH_INDUSTRY,
  TRAVELS_AND_HOSPITALITY_INDUSTRY,
  UNIVERSITY_OR_SCHOOL_INDUSTRY,
} from "../constants.ts";
import type {
  CustomizationStep,
  Industry,
  UserCustomization,
} from "../types.ts";
import { computeUserCustomizationStepProgress } from "../utils/computeUserCustomizationStepProgress.ts";
import { OptionsLayout } from "./OptionsLayout.tsx";
import { UserOnboardingFormLayout } from "./UserOnboardingFormLayout.tsx";

type IndustryWithoutOther = Exclude<Industry, typeof OTHER_INDUSTRY>;

export const CustomizationOnboardingForm = ({
  onComplete,
}: {
  onComplete: () => void;
}) => {
  const [customizationStep, setCustomizationStep] = useState<CustomizationStep>(
    CUSTOMIZATION_STEP_INDUSTRY,
  );
  const [userCustomization, setUserCustomization] = useState<UserCustomization>(
    {
      industry: undefined,
      occupation: undefined,
    },
  );
  const { updateUser, isLoading: isUserUpdateLoading } = useUpdateUser();

  const isOnOccupationStep =
    customizationStep === CUSTOMIZATION_STEP_OCCUPATION;
  const isOnIndustryStep = customizationStep === CUSTOMIZATION_STEP_INDUSTRY;
  const hasSelectedIndustry = Boolean(userCustomization.industry);
  const hasSelectedOtherIndustry =
    userCustomization.industry === OTHER_INDUSTRY;
  const shouldShowOccupationForm =
    isOnOccupationStep &&
    hasSelectedIndustry &&
    !hasSelectedOtherIndustry &&
    userCustomization.industry !== undefined;

  const updateUserAndComplete = useCallback(() => {
    updateUser({
      industry: userCustomization.industry,
      occupation: userCustomization.occupation,
    });
    onComplete();
  }, [updateUser, onComplete, userCustomization]);

  const [errors, setErrors] = useState<{
    industry: string[];
    occupation: string[];
  }>({
    industry: [],
    occupation: [],
  });

  const areAnyErrors =
    (isOnIndustryStep && errors.industry.length > 0) ||
    (isOnOccupationStep && errors.occupation.length > 0);

  const [shouldDisplayErrors, setShouldDisplayErrors] = useState(false);

  const handleContinue = () => {
    if (areAnyErrors) {
      setShouldDisplayErrors(true);
      return;
    }

    if (isOnOccupationStep) {
      updateUserAndComplete();
    } else {
      if (hasSelectedOtherIndustry) {
        updateUserAndComplete();
      } else {
        setShouldDisplayErrors(false);
        setCustomizationStep(CUSTOMIZATION_STEP_OCCUPATION);
      }
    }
  };

  useEffect(() => {
    setErrors({
      industry: userCustomization.industry ? [] : ["Please select an industry"],
      occupation: userCustomization.occupation
        ? []
        : ["Please select an occupation"],
    });
  }, [userCustomization.industry, userCustomization.occupation]);

  return (
    <UserOnboardingFormLayout
      title={
        isOnOccupationStep && hasSelectedIndustry && userCustomization.industry
          ? `What's your area of focus in ${userCustomization.industry.toLowerCase()}?`
          : "What best describes your industry?"
      }
      step={CUSTOMIZATION_STEP}
      progress={computeUserCustomizationStepProgress(userCustomization)}
      onContinue={handleContinue}
      isLoading={isUserUpdateLoading}
    >
      {shouldShowOccupationForm &&
      userCustomization.industry &&
      userCustomization.industry !== OTHER_INDUSTRY ? (
        <OptionsLayout errors={shouldDisplayErrors ? errors.occupation : []}>
          {JOB_OPTIONS[userCustomization.industry].map((job) => (
            <RadioButton
              key={job}
              label={job}
              checked={userCustomization.occupation === job}
              onChange={() =>
                setUserCustomization({ ...userCustomization, occupation: job })
              }
            />
          ))}
        </OptionsLayout>
      ) : (
        <OptionsLayout errors={shouldDisplayErrors ? errors.industry : []}>
          {INDUSTRY_OPTIONS.map((industry) => (
            <RadioButton
              key={industry}
              label={industry}
              checked={userCustomization.industry === industry}
              onChange={() =>
                setUserCustomization({ ...userCustomization, industry })
              }
            />
          ))}
        </OptionsLayout>
      )}
    </UserOnboardingFormLayout>
  );
};

const JOB_OPTIONS: Record<IndustryWithoutOther, string[]> = {
  [ANIMATION_AND_GAMING_INDUSTRY]: [
    "3D Modeler or 3D Designer",
    "Animator",
    "Character Designer",
    "Creative or Artistic Director",
    "Game Designer",
    "Producer",
    "Storyboard Artist",
    "Other",
  ],
  [CREATIVE_OR_COMMUNICATION_AGENCIES_INDUSTRY]: [
    "Brand Strategist or Manager",
    "Content Creator",
    "Copywriter",
    "Creative or Artistic Director",
    "Graphic Designer",
    "Illustrator",
    "Marketing Manager",
    "Product Manager",
    "Project Manager",
    "C-level",
    "Other",
  ],
  [UNIVERSITY_OR_SCHOOL_INDUSTRY]: ["Professor", "Student", "Other"],
  [FASHION_INDUSTRY]: [
    "Brand Manager",
    "Content Creator",
    "Creative or Artistic Director",
    "Fashion Designer",
    "Fashion Illustrator",
    "Stylist",
    "Textile Designer",
    "Other",
  ],
  [RETAIL_INDUSTRY_AND_CONSUMER_GOODS]: [
    "Brand Manager",
    "Content Creator",
    "Creative or Artistic Director",
    "Marketing Manager",
    "Other",
  ],
  [BEAUTY_AND_SKINCARE_INDUSTRY]: [
    "CEO / Founder",
    "Marketing Manager",
    "Social Media Manager",
    "Brand Manager",
    "Creative or Artistic Director",
    "Content Creator",
    "Designer",
    "Sales Team",
    "Product Team",
    "Other",
  ],
  [TRAVELS_AND_HOSPITALITY_INDUSTRY]: [
    "CEO / Founder",
    "Marketing Manager",
    "Social Media Manager",
    "Brand Manager",
    "Creative or Artistic Director",
    "Content Creator",
    "Designer",
    "Sales Team",
    "Product Team",
    "Other",
  ],
  [FOOD_AND_BEVERAGE_INDUSTRY]: [
    "CEO / Founder",
    "Marketing Manager",
    "Social Media Manager",
    "Brand Manager",
    "Creative or Artistic Director",
    "Content Creator",
    "Designer",
    "Sales Team",
    "Product Team",
    "Other",
  ],
  [TECH_INDUSTRY]: [
    "CEO / Founder",
    "Marketing Manager",
    "Social Media Manager",
    "Brand Manager",
    "Creative or Artistic Director",
    "Content Creator",
    "Designer",
    "Sales Team",
    "Product Team",
    "Other",
  ],
  [REAL_ESTATE_INDUSTRY]: [
    "CEO / Founder",
    "Marketing Manager",
    "Social Media Manager",
    "Brand Manager",
    "Creative or Artistic Director",
    "Content Creator",
    "Designer",
    "Sales Team",
    "Product Team",
    "Other",
  ],
};
